import axios from 'axios';
import alert_mixin from '@/utils/mixins/alert_mixin';
import {
  CREATE_SERVICE_MAPPINGS,
  GET_QUESTION_MAPPINGS,
  GET_MAPPING_OPTIONS,
  GET_VERIFICATION_SERVICES,
  get_request_config,
} from '@/utils/system_constants/constant';

export default {
  name: 'ConfigureVerificationServices',
  mixins: [alert_mixin],
  data: () => ({
    loading: false,
    loading_mapping_options: false,
    mapping_options_message: '',
    services: [],
    selected_service_id: null,
    selected_service: null,
    verification_form_id: null,
    required_fields: [],
    questions: [],
    field_mappings: {},
    saving_configuration: false,
  }),
  mounted() {
    this.selected_service_id = this.$route?.query?.serviceId ? Number(this.$route.query.serviceId) : null;
    this.verification_form_id = this.$route?.query?.verification_form_id
      ? Number(this.$route.query.verification_form_id)
      : null;
    this.load_selected_service();
  },
  computed: {
    grouped_questions() {
      const items = Array.isArray(this.questions) ? this.questions : [];
      const groups = new Map();

      items.forEach((question_item) => {
        const section = (question_item?.section || 'Other').trim() || 'Other';
        const group_items = groups.get(section) || [];
        group_items.push(question_item);
        groups.set(section, group_items);
      });

      return Array.from(groups.entries())
        .map(([section, group_items]) => ({
          section,
          items: group_items
            .slice()
            .sort((first_item, second_item) =>
              String(first_item?.label || '').localeCompare(String(second_item?.label || ''), undefined, {
                sensitivity: 'base',
              }),
            ),
        }))
        .sort((first_group, second_group) =>
          String(first_group.section).localeCompare(String(second_group.section), undefined, { sensitivity: 'base' }),
        );
    },
  },
  methods: {
    format_required_field_label(field) {
      const raw = field?.label ?? field?.name ?? field;
      if (raw == null) return '';
      const text = String(raw)
        .replace(/[_]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      return text.toLowerCase();
    },

    load_selected_service() {
      this.loading = true;

      return axios
        .get(GET_VERIFICATION_SERVICES, get_request_config())
        .then((response) => {
          if (response.data?.status !== 'success') {
            this.show_error(response.data?.message || 'Failed to load verification services');
            return;
          }

          const services = Array.isArray(response.data?.data) ? response.data.data : [];
          this.services = services;

          const selected_service = services.find(
            (service_item) => String(service_item?.id) === String(this.selected_service_id),
          );
          this.selected_service = selected_service || null;

          // If backend provides required fields, use it; otherwise leave empty for now.
          const required = selected_service?.required_fields || selected_service?.requiredFields || [];
          this.required_fields = Array.isArray(required) ? required : [];

          return this.load_mapping_options();
        })
        .catch((error) => {
          this.show_error(error?.message || 'Failed to load verification service');
        })
        .finally(() => {
          this.loading = false;
        });
    },

    load_mapping_options() {
      if (!this.selected_service_id || !this.verification_form_id) {
        this.questions = [];
        this.field_mappings = {};
        this.mapping_options_message = '';
        return Promise.resolve();
      }

      this.loading_mapping_options = true;
      this.mapping_options_message = '';

      return axios
        .get(GET_MAPPING_OPTIONS, get_request_config({
          params: {
            service_id: this.selected_service_id,
            verification_form_id: this.verification_form_id,
          },
        }))
        .then((response) => {
          const data = response?.data ?? {};

          if (data.service) {
            this.selected_service = { ...this.selected_service, ...data.service };
            this.required_fields = data.service.required_fields ?? [];
          }

          this.questions = (data.questions ?? [])
            .map(({ id, question_name, section }) => ({
              id,
              label: question_name ?? 'Untitled question',
              section: section ?? '',
            }))
            .filter(({ id }) => id != null);

          return this.load_question_mappings();
        })
        .catch(() => {
          this.questions = [];
        })
        .finally(() => {
          this.loading_mapping_options = false;
        });
    },

    load_question_mappings() {
      if (!this.selected_service_id || !this.verification_form_id) {
        this.field_mappings = {};
        return Promise.resolve();
      }

      return axios
        .get(
          GET_QUESTION_MAPPINGS,
          get_request_config({
            params: {
              service_id: this.selected_service_id,
              verification_form_id: this.verification_form_id,
            },
          }),
        )
        .then((response) => {
          const data = response?.data ?? {};
          if (data?.status && data.status !== 'success') {
            this.field_mappings = {};
            return;
          }

          const mappings = Array.isArray(data?.mappings) ? data.mappings : [];
          const allowed_question_ids = new Set(
            (this.questions || []).map((question_item) => String(question_item?.id)).filter(Boolean),
          );
          const next = { ...(this.field_mappings || {}) };

          mappings.forEach((mapping_item) => {
            const required_field = mapping_item?.required_field;
            const question_id = mapping_item?.question_id;
            if (!required_field) return;
            if (question_id == null) return;
            if (allowed_question_ids.size && !allowed_question_ids.has(String(question_id))) return;
            next[String(required_field)] = String(question_id);
          });

          this.field_mappings = next;
        })
        .catch(() => {
          // Non-fatal: allow user to map manually even if preload fails.
        });
    },

    on_field_mapping_change(field, evt) {
      const key = field?.key || field?.name || String(field || '');
      const value = evt?.target?.value || '';
      this.field_mappings = {
        ...(this.field_mappings || {}),
        [key]: value,
      };
    },

    save_configuration() {
      if (!this.selected_service_id || !this.verification_form_id) {
        this.show_error('Missing service or verification form id.');
        return;
      }

      const required_fields = Array.isArray(this.required_fields) ? this.required_fields : [];
      const field_mappings = this.field_mappings || {};

      const mappings = required_fields
        .map((field) => {
          const required_field = field?.key || field?.name || String(field || '');
          const question_id_raw = Reflect.get(field_mappings, required_field);
          const question_id = question_id_raw !== '' && question_id_raw != null ? Number(question_id_raw) : null;
          return {
            service_id: Number(this.selected_service_id),
            verification_form_id: Number(this.verification_form_id),
            required_field,
            question_id,
            field_type: 'api',
          };
        })
        .filter((mapping_item) => mapping_item.required_field);

      const missing_required_fields = mappings
        .filter((mapping_item) => !mapping_item.question_id || Number.isNaN(mapping_item.question_id))
        .map((mapping_item) => mapping_item.required_field);

      if (missing_required_fields.length) {
        this.show_error(`Please select a question for: ${missing_required_fields.join(', ')}`);
        return;
      }

      this.saving_configuration = true;
      return this.show_loader('Saving mappings...')
        .then((loader) =>
          axios
            .post(CREATE_SERVICE_MAPPINGS, { mappings }, get_request_config())
            .then((response) => {
              const data = response?.data ?? {};
              if (data?.status === 'success') {
                this.show_success('Mappings saved successfully.');
                return;
              }
              this.show_error(data?.message || 'Failed to save mappings.');
            })
            .catch((error) => {
              this.show_error(error?.response?.data?.message || error?.message || 'Failed to save mappings.');
            })
            .finally(async () => {
              await this.close_loader(loader);
              this.saving_configuration = false;
            }),
        )
        .catch(() => {
          // If loader fails for any reason, still attempt the save without blocking UX.
          this.saving_configuration = false;
        });
    },

    go_back() {
      if (this.$router) this.$router.back();
    },
  },
};
